import type { TranslationTag } from '@ps/web-core-intl';
import { Translation } from '@ps/web-shared-components';
import { StarsInfo } from '@ps/web-shared-ui';
import classNames from 'clsx';
import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { ModalContext } from '@swf/components/StarsModal/Context';
import Body from '@swf/components/StarsModal/components/Body';
import useSearchParams from '@swf/core/hooks/useSearchParams';
import { select } from '@swf/core/store';
import { useHomeGamesWidget, useWidgetTheme } from '@swf/poker';

import Header from '../../../../Modal/Header/Header';
import ShareClub from '../../../../ShareClub/ShareClub';
import SuccessIcon from '../../assets/success.svg';

import {
  TXTCLI_CONGRATULATIONSX,
  TXTCLI_Club_ID,
  TXTCLI_Invitation_Code,
  TXTWCL_Copy_Instructions,
  TXTWCL_Please_send_the_instructions_t_ELL,
  TXTWCL_Share_Club,
  TXTWCL_XspanXN0X_spanX_has_been_succe_ELL
} from './CreateClubSuccess.messages.json';
import styles from './CreateClubSuccess.scss';

export interface CreateClubSuccessParams {
  clubId: number;
  invitationCode: string;
  clubName?: string;
  ignoreDataFetch?: boolean;
  widgetId?: string;
  title?: TranslationTag;
}

export const CreateClubSuccess: React.FC = () => {
  const { widgetId, clubId, clubName, invitationCode, ignoreDataFetch, title } =
    useSearchParams<CreateClubSuccessParams>();
  const { setStyle } = useContext(ModalContext);
  const { widgetDispatch } = useHomeGamesWidget(widgetId);
  const isMobile = useSelector(select.device.isMobile);
  const titleTag = title ?? TXTCLI_CONGRATULATIONSX;

  const shouldUseShareTranslation = isMobile && navigator.share;
  const { className } = useWidgetTheme();

  useEffect(() => {
    setStyle({
      overlayClassName: className,
      className: styles.createClubSuccessModal,
      closeOnOverlayClick: true,
      closeOnEsc: true
    });
  }, [className, setStyle]);

  useEffect(
    () => () => {
      if (ignoreDataFetch) return;

      widgetDispatch.fetchUserClubs();
    },
    [widgetDispatch, ignoreDataFetch]
  );

  return (
    <>
      <Header title={<Translation {...titleTag} />} />
      <Body className={styles.body}>
        <div
          className={classNames(styles.container, { [styles.containerMobile]: isMobile })}
          data-testid="create-club-success-modal"
        >
          {clubName && (
            <StarsInfo type="confirmation" className={styles.successPanel}>
              <div className={styles.icon}>
                <SuccessIcon />
              </div>
              <div>
                <Translation
                  {...TXTWCL_XspanXN0X_spanX_has_been_succe_ELL}
                  values={{ 0: clubName }}
                />
              </div>
            </StarsInfo>
          )}
          <div className={styles.divider} />
          <div className={styles.clubDetails}>
            <Translation {...TXTCLI_Club_ID} />:<span>{clubId}</span>
          </div>
          {invitationCode && (
            <>
              <div className={styles.divider} />
              <div className={styles.clubDetails}>
                <Translation {...TXTCLI_Invitation_Code} />:<span>{invitationCode}</span>
              </div>
            </>
          )}

          <div className={styles.divider} />
          <p className={styles.sendInstructionsText}>
            <Translation {...TXTWCL_Please_send_the_instructions_t_ELL} />
          </p>
          <ShareClub
            clubId={Number(clubId)}
            invitationCode={invitationCode || ''}
            buttonProps={{
              id: 'copyInstructions',
              translation: shouldUseShareTranslation ? TXTWCL_Share_Club : TXTWCL_Copy_Instructions,
              type: 'submit'
            }}
          />
        </div>
      </Body>
    </>
  );
};

export default CreateClubSuccess;
